import React, {useState, useRef, useEffect} from 'react'
import './ColorPicker.less'

export interface ColorPickerProps {
    color: string
    onChange: (color: string) => void
    onCancel: () => void
    allowTransparent?: boolean
}

// Using brand colors from theme as presets
const presetColors = [
    "#ffffff",
    "#a6ccf5",
    "#fff9b1",
    "#ffcee0",
    "#d5f692",
    "#93d275",
    "#f5f6f8",
    "#e5e6e8",
    "#333333",
    "#FCCB1A",
]

export const ColorPicker: React.FC<ColorPickerProps> = ({
                                                            color,
                                                            onChange,
                                                            onCancel,
                                                            allowTransparent = false
                                                        }) => {
    const [inputValue, setInputValue] = useState<string>(color)
    const [isOpen, setIsOpen] = useState<boolean>(false)
    const wrapperRef = useRef<HTMLDivElement>(null)

    // Include transparent if allowed
    const allColors = allowTransparent ? [...presetColors, 'transparent'] : presetColors

    const handleColorSelect = (selectedColor: string) => {
        onChange(selectedColor)
        setInputValue(selectedColor)
        setIsOpen(false)
    }

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value
        setInputValue(value)
    }

    const handleInputBlur = () => {
        // Validate and update color when input loses focus
        onChange(inputValue)
    }

    const handleInputKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            onChange(inputValue)
        } else if (e.key === 'Escape') {
            onCancel()
        }
    }

    // Close dropdown when clicking outside
    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
                setIsOpen(false)
            }
        }

        document.addEventListener('mousedown', handleClickOutside)
        return () => {
            document.removeEventListener('mousedown', handleClickOutside)
        }
    }, [])

    return (
        <div className="color-picker" ref={wrapperRef}>
            <div className="color-picker-input-wrapper">
                <input
                    type="text"
                    value={inputValue}
                    onChange={handleInputChange}
                    onBlur={handleInputBlur}
                    onKeyDown={handleInputKeyPress}
                    className="color-picker-input"
                    placeholder="Enter color..."
                />
                <button
                    className="color-picker-toggle"
                    onClick={() => setIsOpen(!isOpen)}
                >
                    ▼
                </button>
            </div>

            {isOpen && (
                <div className="color-picker-dropdown">
                    <div className="color-picker-presets">
                        {allColors.map((presetColor, index) => (
                            <div
                                key={index}
                                className={`color-picker-preset ${presetColor === color ? 'selected' : ''}`}
                                style={{
                                    backgroundColor: presetColor !== 'transparent' ? presetColor : '#FFFFFF',
                                    border: presetColor === 'transparent' ? '1px dashed #999' : '1px solid #e0e0e0'
                                }}
                                onClick={() => handleColorSelect(presetColor)}
                                title={presetColor}
                            >
                                {presetColor === 'transparent' && (
                                    <div className="transparent-pattern"></div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    )
}
